import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { defaultPeriodMonths } from "../../lib/scenario";
import { unionMonths, currentMonthKey, monthRange, addMonths } from "../../lib/dateWindow";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import api from "../../lib/api";
import { fmtMoney } from "../../lib/format";
import { DRIVER_RATE_PER_PERSON_MONTH, PROJECT_STATUSES, RISK_FLAGS, CONTRACT_VALUES } from "../../lib/constants";
import { ratesFromData, resolvedPeople } from "../../lib/personnel";
import { mergedAllocations } from "../../lib/allocations";
import { grossNetSub } from "../../lib/subconsultants";
import { newSubset, reorderScopes } from "../../lib/subsets";
import { CURRENCIES, ECB_UNSUPPORTED, fxLiveLabel, liveRateOf } from "../../lib/currency";
import { useFx } from "../../lib/useFx";
import { ScopeGrid } from "../../components/project/ScopeGrid";
import { SubConsultantsGrid } from "../../components/project/SubConsultantsGrid";
import { NetFeeStrip } from "../../components/project/NetFeeStrip";
import { PersonnelAllocations } from "../../components/project/PersonnelAllocations";
import { InvoiceActuals } from "../../components/project/InvoiceActuals";
import { ProfitabilityBar } from "../../components/project/ProfitabilityBar";
import { toast } from "sonner";
import InfoTip from "../../components/InfoTip";
import { Save, ArrowLeft, AlertTriangle } from "lucide-react";

const LEAVE_MSG = "You have unsaved changes on this project. Leave without saving?";

// Stages come from data.meta.stages (seeded from backend/seed_constants.STAGES,
// kept in sync via the dataset itself rather than a duplicated frontend list).

function genId() {
  const yr = new Date().getFullYear();
  return `${yr}_${Math.floor(1000 + Math.random()*9000)}`;
}

function emptyScope(months, stages, defaultContract = "Tentative") {
  return {
    id: `s_${Math.random().toString(36).slice(2, 8)}`,
    stage: "",
    feeType: "fixed",
    probability: 1.0,
    contract: defaultContract,
    contractValue: 0,
    note: "",
    revenue: Object.fromEntries(months.map((m) => [m, 0])),
    revenueRatios: {},
  };
}

function emptySubRow(months) {
  return {
    id: `sub_${Math.random().toString(36).slice(2, 8)}`,
    stage: "Sub consultants",
    isSubConsultant: true,
    // Sub rows follow their own contract status like any other scope row.
    // Confirmed by default so a pass-through deduction always shows against
    // the signed position; change it on the row if the fee is still tentative.
    contract: "Yes",
    probability: 1.0,
    contractValue: 0,
    note: "",
    revenue: Object.fromEntries(months.map((m) => [m, 0])),
    revenueRatios: {},
  };
}

// Derive a project-level contract status from its scopes
// (used for legacy admin views that read project.contract).
// Sub-consultant rows are pass-through money — they don't set contract status.
function deriveContract(scopes) {
  const vals = (scopes || []).filter((s) => !s.isSubConsultant).map((s) => s.contract || "Tentative");
  if (vals.includes("Yes")) return "Yes";
  if (vals.includes("Tentative")) return "Tentative";
  return vals.length ? "No" : "Yes";
}

export default function ProjectForm() {
  const { rawData, refresh, viewAs } = useData();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const { projectId } = useParams();
  // CRITICAL: the form edits the RAW dataset, never the scenario-applied one.
  // `data` from useData() has probabilities baked to 1 (and revenue scaled
  // under the weighted scenario) plus period-narrowed months — saving from it
  // silently destroys probabilities. This was the root cause of the recurring
  // "weighted totals drift" bug.
  // Same form is mounted under /portfolio (principals) and /admin/my-projects
  // (admins). Detect which context so the back link, post-save redirect, and
  // the linkedPrincipal dropdown all behave correctly.
  const inAdmin = loc.pathname.startsWith("/admin/");
  const base = inAdmin ? "/admin/my-projects" : "/portfolio/projects";
  const isAdmin = user?.role === "admin";
  const canEditPast = !!user?.canEditPastMonths;

  const editing = useMemo(
    () => rawData?.projects.find((p) => p.id === projectId),
    [rawData, projectId]
  );

  // Month columns visible on the form:
  //   · new project → rolling 12 from the current calendar month (or
  //                    extended if the user picks an Expected End Date)
  //   · editing     → union of stored months, the project's own extended
  //                    window, and any months where this project already
  //                    has data. Lets principals extend into future
  //                    months and keeps past invoiced months editable.
  //
  // Project-level extended window (Feb 2026): the form grids stretch from
  // the as-at month through max(as-at + 11, Expected End Date's month).
  // Blank endDate → defaults to as-at + 23 (~24 months) so long-horizon
  // projects like Helier 2 have room without editing the date first.
  const projectMonthKeys = useMemo(() => {
    if (!editing) return [];
    const keys = new Set();
    for (const s of editing.scopes || []) {
      Object.keys(s.revenue || {}).forEach((k) => keys.add(k));
      Object.keys(s.invoiced || {}).forEach((k) => keys.add(k));
    }
    for (const a of editing.allocations || []) {
      Object.keys(a.load || {}).forEach((k) => keys.add(k));
      Object.keys(a.approvedValues || {}).forEach((k) => keys.add(k));
      Object.keys(a.proposedValues || {}).forEach((k) => keys.add(k));
    }
    return Array.from(keys);
  }, [editing]);
  const rolling = useMemo(() => defaultPeriodMonths(), []);
  const stored = useMemo(() => rawData?.meta?.months || [], [rawData]);

  // Extended window driven by Expected End Date. Falls back to 24 months
  // from now when the date is blank so long-horizon projects don't need
  // an explicit date to have room. Reactive to form.endDate below.
  const extendedFromEndDate = (endDateIso) => {
    const cur = currentMonthKey();
    const endKey = (endDateIso && /^\d{4}-\d{2}/.test(endDateIso))
      ? endDateIso.slice(0, 7)
      : null;
    // count = months(cur → max(cur+11, endKey)) inclusive. Default 24 when blank.
    let count = endKey ? 12 : 24;
    if (endKey) {
      const [ey, em] = endKey.split("-").map(Number);
      const [cy, cm] = cur.split("-").map(Number);
      const monthsBetween = (ey - cy) * 12 + (em - cm) + 1;
      count = Math.max(12, monthsBetween);
    }
    return monthRange(cur, count);
  };

  const initialExtended = useMemo(
    () => extendedFromEndDate(editing?.endDate),
    [editing],
  );
  const initialM = useMemo(
    () => unionMonths(unionMonths(stored, initialExtended), projectMonthKeys),
    [stored, initialExtended, projectMonthKeys],
  );
  const STAGES = rawData?.meta?.stages || [];

  const [form, setForm] = useState(() => editing ? structuredClone(editing) : {
    id: "",
    name: "",
    client: "",
    startDate: "",
    endDate: "",
    status: "In Progress",
    riskFlag: "on_track",
    contract: "Tentative",
    // Admins creating from My Projects default to their own principal — or the
    // viewed principal while view-as is active (server enforces the same).
    principalId: viewAs || user?.principalId || "",
    note: "",
    scopes: [emptyScope(initialM, rawData?.meta?.stages || [])],
    peopleNeededPerMonth: Object.fromEntries(initialM.map((mo) => [mo, 0])),
    tierResourcing: [],
    subConsultants: [],
    allocations: [],
    editors: [],
    actualInvoiced: {},
    invoicingConfirmed: {},
  });

  // Reactive month window — recomputes when the user picks a different
  // Expected End Date. All grids (forecast, resourcing, invoiced) read
  // from this so they extend/contract together.
  const m = useMemo(
    () => unionMonths(unionMonths(stored, extendedFromEndDate(form?.endDate)), projectMonthKeys),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stored, form?.endDate, projectMonthKeys],
  );

  // Sync form when editing project arrives async. Backfill any missing
  // per-scope defaults (older data) without inheriting from the derived
  // project-level `contract` — every scope row holds its OWN status.
  useEffect(() => {
    if (!editing) return;
    const cloned = structuredClone(editing);
    cloned.scopes = (cloned.scopes || []).map((s) => {
      // NEVER inherit from cloned.contract here — that was collapsing every
      // scope to the derived project status on load, then bleeding back
      // through the save path. Tentative is the safe default for scopes
      // stored without their own contract; sub-consultant pass-through rows
      // default to Confirmed so a deduction always shows against the signed
      // position.
      const fallback = s.isSubConsultant ? "Yes" : "Tentative";
      return {
        contract: s.contract || fallback,
        note: "",
        feeType: "fixed",
        contractValue: 0,
        revenueFlags: {},
        ...s,
        // Force the resolved contract to survive spread of a stored s that
        // has contract=undefined explicitly.
        ...(s.contract ? {} : { contract: fallback }),
      };
    });
    cloned.peopleNeededPerMonth = cloned.peopleNeededPerMonth || {};
    cloned.subConsultants = cloned.subConsultants || [];
    cloned.editors = cloned.editors || [];
    cloned.actualInvoiced = cloned.actualInvoiced || {};
    cloned.invoicingConfirmed = cloned.invoicingConfirmed || {};
    // Project-level invoicing note retired — superseded by the per-row
    // Notes column on the invoicing grid.
    delete cloned.invoicingNote;
    delete cloned.invoicingNoteEditedAt;
    delete cloned.invoicingNoteEditedBy;
    // MERGE: fold any legacy `tierResourcing` rows into `allocations` as
    // TBD-tier rows so the single merged grid is the source of truth going
    // forward. Existing named-person rows carry through untouched. Clear
    // tierResourcing so it can't double-count on save.
    cloned.allocations = mergedAllocations(cloned).map((a) => ({
      ...a,
      // Named rows still edit their PENDING values when a proposal is in
      // flight; TBD/migrated rows already carry approvedValues=load.
      load: (a.status === "proposed" ? a.proposedValues : a.load) || {},
    }));
    cloned.tierResourcing = [];
    setForm(cloned);
    savedRef.current = JSON.stringify(cloned);
    setDirty(false);
  }, [editing]);

  const [busy, setBusy] = useState(false);
  // Last save failure, shown as a persistent banner beside Save. Survives
  // until the next SUCCESSFUL save — a rejected save leaves every edit on
  // screen, so the message has to stay on screen with them.
  const [saveError, setSaveError] = useState(null);
  // Saved snapshot for unsaved-changes detection. Autosave was removed (firm
  // request, 2026-06): the form now writes ONLY when Save is pressed.
  const savedRef = useRef(null);
  const [dirty, setDirty] = useState(false);

  // ---------- Auto-calc ----------
  const totalFee = useMemo(() => form.scopes.reduce(
    (sum, s) => sum + Object.values(s.revenue || {}).reduce((a,b)=>a+(b||0), 0), 0
  ), [form.scopes]);

  // Personnel needed per month is now derived from the merged allocations
  // grid (named + TBD tier rows). Approved values drive capacity — for the
  // header total on the form we sum the current load values.
  const rates = useMemo(() => ratesFromData(rawData), [rawData]);
  // Capacity rows with their tier resolved from the linked personnel record
  // where the row itself carries none — keeps resourcing priced at the real
  // tier average rather than the flat driver rate.
  const people = useMemo(() => resolvedPeople(rawData), [rawData]);
  const derivedPpl = useMemo(() => {
    const out = Object.fromEntries(m.map((mo) => [mo, 0]));
    for (const a of form.allocations || []) {
      if (a.status === "rejected") continue;
      for (const mo of m) out[mo] = Math.round((out[mo] + (a.load?.[mo] || 0)) * 10) / 10;
    }
    return out;
  }, [form.allocations, m]);

  // Gross / sub-consultant / net across the visible month window — feeds the
  // NET strip above resourcing. Display only; nothing stored.
  const netFigures = useMemo(() => grossNetSub(form.scopes, m), [form.scopes, m]);

  // Live exchange rate (ECB day, cached server-side). It only ever PREFILLS
  // this project's rate — a locked rate is never restated, and every invoiced
  // or past-month figure keeps converting at the rate it was locked at.
  const fx = useFx();
  const liveFxRate = liveRateOf(form, fx);
  const applyLiveFxRate = () => setForm((f) => ({
    ...f,
    fxRate: liveFxRate,
    fxRateSetAt: new Date().toISOString(),
    fxRateSetBy: user?.email || "",
    fxRateSource: `ECB as at ${fx?.date || "—"}`,
    fxRateHistory: [
      ...(f.fxRateHistory || []),
      ...(f.fxRate && Number(f.fxRate) !== liveFxRate
        ? [{ rate: Number(f.fxRate), currency: f.currency, replacedAt: new Date().toISOString(), replacedBy: user?.email || "" }]
        : []),
    ],
  }));

  // Monthly forecast totals from the scope grid (feeds actuals-vs-forecast).
  const monthlyForecast = useMemo(() => {
    const out = Object.fromEntries(m.map((mo) => [mo, 0]));
    for (const s of form.scopes) for (const mo of m) out[mo] += s.revenue?.[mo] || 0;
    return out;
  }, [form.scopes, m]);

  // ---------- Field updates ----------
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addScope = () => setForm((f) => ({ ...f, scopes: [...f.scopes, emptyScope(m, STAGES)] }));
  // One row per sub-consultant — a project can carry several (structural,
  // services, fire…). The old guard refused to add a second row, so the button
  // silently did nothing once one row existed.
  const addSubRow = () => setForm((f) => ({ ...f, scopes: [...f.scopes, emptySubRow(m)] }));
  const removeScope = (idx) => {
    const target = form.scopes[idx];
    const kids = form.scopes.filter((k) => k.parentScopeId === target.id);
    // Ask BEFORE setForm — an updater can be re-run by React, which would
    // prompt the user twice.
    const deleteAll = kids.length
      ? window.confirm(
          `"${target.stage || "This stage"}" has ${kids.length} subset row(s).\n\n` +
          `OK — delete the stage AND its subsets.\n` +
          `Cancel — keep the subsets, promoting them to their own stage rows.`
        )
      : true;
    setForm((f) => {
      if (!kids.length || deleteAll) {
        return { ...f, scopes: f.scopes.filter((s, i) => i !== idx && (!kids.length || s.parentScopeId !== target.id)) };
      }
      return {
        ...f,
        scopes: f.scopes
          .filter((_, i) => i !== idx)
          .map((s) => (s.parentScopeId === target.id
            ? { ...s, parentScopeId: undefined, subsetLabel: undefined, note: s.note || s.subsetLabel || "" }
            : s)),
      };
    });
  };

  const addSubset = (parentIdx) => setForm((f) => {
    const parent = f.scopes[parentIdx];
    const kids = f.scopes.filter((s) => s.parentScopeId === parent.id);
    let insertAt = parentIdx + 1;
    f.scopes.forEach((s, i) => { if (s.parentScopeId === parent.id) insertAt = i + 1; });
    const next = [...f.scopes];
    next.splice(insertAt, 0, newSubset(parent, m, kids.length));
    return { ...f, scopes: next };
  });

  // Save-time split: legacy tier rows (loaded via mergedAllocations) go back
  // to the server's `tierResourcing` array; named people and fresh TBDs go
  // to `allocations`. Keeps the approval queue clean and the capacity plan
  // for legacy projects unchanged.
  const splitAllocations = (all) => {
    const allocations = [];
    const tierResourcing = [];
    for (const a of all || []) {
      if (a.migratedFromTierResourcing) {
        tierResourcing.push({
          id: a.tierResourcingId || `tr_${Math.random().toString(36).slice(2, 8)}`,
          tier: a.tier,
          load: { ...(a.load || {}) },
        });
      } else {
        const { migratedFromTierResourcing: _mf, tierResourcingId: _ti, ...rest } = a;
        allocations.push(rest);
      }
    }
    return { allocations, tierResourcing };
  };

  // ---------- Save ----------
  const save = async () => {
    if (!form.name.trim()) { toast.error("Project name required"); return; }
    setBusy(true);
    try {
      const id = form.id?.trim() || genId();
      // Drop untouched (all-zero) sub-consultant rows.
      // Sub-consultant fees are pass-through and are routinely entered as
      // NEGATIVE values — the test must be "non-zero", never "> 0", or every
      // negative sub row is silently discarded on save.
      const nonZero = (map) => Object.values(map || {}).some((v) => (Number(v) || 0) !== 0);
      const scopes = form.scopes.filter((s) =>
        !s.isSubConsultant ||
        (Number(s.contractValue) || 0) !== 0 ||
        nonZero(s.revenue) ||
        nonZero(s.revenueRatios) ||
        nonZero(s.invoiced)
      );
      // Save-time split: legacy tier-resourcing rows migrated into the
      // merged grid on load carry a `migratedFromTierResourcing` marker.
      // Send them back to the server's `tierResourcing` array so the
      // approval queue doesn't flood on save and legacy capacity is
      // preserved. Fresh TBDs and named people go to `allocations`.
      const { allocations, tierResourcing } = splitAllocations(form.allocations);
      // Past months of the headcount plan are never rewritten from the
      // current allocation grid: the server gates them, and a note-only save
      // must leave them exactly as stored. Stored past values carry through
      // untouched, and past keys the record never held are not invented.
      const curM = currentMonthKey();
      const ppl = { ...derivedPpl };
      if (!canEditPast) {
        for (const k of Object.keys(ppl)) if (k < curM) delete ppl[k];
        for (const [k, v] of Object.entries(editing?.peopleNeededPerMonth || {})) {
          if (k < curM) ppl[k] = v;
        }
      }
      const payload = {
        ...form, id, scopes, allocations, tierResourcing,
        contract: deriveContract(scopes),
        peopleNeededPerMonth: ppl,
      };
      const wasNew = !editing;
      if (editing) {
        await api.put(`/data/projects/${encodeURIComponent(id)}`, payload);
        toast.success("Project saved");
      } else {
        await api.post("/data/projects", payload);
        toast.success("Project created");
      }
      setSaveError(null);
      savedRef.current = JSON.stringify(form);
      setDirty(false);
      // If an admin just created their first project with no linked principal,
      // the server auto-linked them — pick up the new principalId here so
      // subsequent loads of My Projects show the project immediately.
      if (wasNew && isAdmin && !user?.principalId) {
        await refreshUser();
      }
      await refresh();
      navigate(base);
    } catch (e) {
      // Persistent banner beside Save, not a toast that fades: the edits are
      // still on screen and unsaved, and the user must be able to read why
      // long after the attempt. Cleared only by a successful save.
      setSaveError(e.response?.data?.detail
        || "Could not save the project — check your connection and try again");
    } finally {
      setBusy(false);
    }
  };

  // Autosave removed (firm request): nothing is written until Save is pressed.
  // Track unsaved state and warn before the edits can be lost.
  useEffect(() => {
    if (savedRef.current === null) { savedRef.current = JSON.stringify(form); return; }
    setDirty(JSON.stringify(form) !== savedRef.current);
  }, [form]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    // In-app navigation: react-router links are plain anchors, so a capture
    // listener can confirm before the route changes.
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a || a.target === "_blank") return;
      if (a.getAttribute("href")?.startsWith("#")) return;
      if (new URL(a.href, window.location.origin).pathname === window.location.pathname) return;
      if (!window.confirm(LEAVE_MSG)) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty]);

  const leaveForm = () => {
    if (dirty && !window.confirm(LEAVE_MSG)) return;
    navigate(base);
  };

  // Wayfinding: name the owning principal in the eyebrow so a screen full
  // of identical-looking columns can never be identified only by its data.
  const owningPrincipal = form.principalId
    ? (rawData?.principals || []).find((pr) => pr.id === form.principalId)?.name
    : null;
  const sectionEyebrow = inAdmin
    ? (owningPrincipal ? `MY PROJECTS · ${owningPrincipal.toUpperCase()}` : "MY PROJECTS")
    : (owningPrincipal ? `PORTFOLIO · ${owningPrincipal.toUpperCase()}` : "PORTFOLIO");

  return (
    <div className="space-y-8" data-testid="project-form">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div className="min-w-0">
          <button onClick={leaveForm} className="eyebrow text-[var(--soft-fg)] hover:text-[var(--ink)] inline-flex items-center gap-1.5 mb-3 transition-colors" data-testid="back-link">
            <ArrowLeft size={11} /> Back to {inAdmin ? "my projects" : "portfolio"}
          </button>
          <div className="eyebrow" data-testid="project-form-eyebrow">{sectionEyebrow}</div>
          <h1 className="h-page mt-1.5" data-testid="project-form-title">{editing ? (form.name || "Untitled project") : "Add new project"}</h1>
          {editing?.lastEditedBy && (
            <div className="t-caption normal-case tracking-[0.06em] mt-2">
              Last edited by {editing.lastEditedBy} · {new Date(editing.lastEditedAt).toLocaleString("en-NZ")}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            {dirty && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-mono-num text-[#8a6d1a] whitespace-nowrap" data-testid="unsaved-indicator">
                <AlertTriangle size={12} /> Unsaved changes
              </span>
            )}
            <button onClick={save} disabled={busy || !dirty}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-display tracked-tight uppercase whitespace-nowrap transition-colors ${
                dirty
                  ? "bg-[var(--ink)] text-white hover:opacity-90 ring-2 ring-offset-2 ring-[var(--ink)]/25"
                  : "bg-transparent text-[var(--soft-fg)] border border-[var(--rule-strong)] cursor-default"
              } disabled:cursor-default`}
              title={dirty ? "Save your changes" : "No changes to save"}
              data-testid="save-project">
              <Save size={12} /> {busy ? "Saving…" : dirty ? "Save project" : "Saved"}
            </button>
          </div>
          {saveError && (
            <div
              role="alert"
              className="flex items-start gap-2 max-w-[28rem] border border-[#6B2F2A]/45 bg-[#FBF3F2] px-3 py-2 text-left"
              data-testid="save-error-banner"
            >
              <AlertTriangle size={13} className="text-[#6B2F2A] mt-0.5 shrink-0" />
              <div className="text-[11px] leading-relaxed">
                <span className="font-display tracked-tight uppercase text-[#6B2F2A]">Not saved</span>
                <span className="font-mono-num text-[#6B2F2A]"> · {saveError}</span>
                <div className="font-mono-num text-[var(--muted-fg)] mt-1" data-testid="save-error-banner-note">
                  Your edits are still on screen and have NOT been saved. Nothing has been
                  written. This message stays until a save succeeds.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Identity */}
      <Section title="Identity">
        <Row>
          <Field label="Project name *">
            <input value={form.name} onChange={(e)=>setField("name", e.target.value)} className={inp} data-testid="field-name" />
          </Field>
          <Field label="Project number" info={editing ? "The display number for this project — safe to rename (e.g. from a placeholder like 2026_0XXX_oceania to 2026_0373). The internal id stays stable and won't break any links." : "Leave blank and we'll generate a project number in the format 2026_XXXX."}>
            <input
              value={editing ? (form.number ?? form.id ?? "") : (form.id || "")}
              onChange={(e) => setField(editing ? "number" : "id", e.target.value)}
              className={inp}
              disabled={editing && !(isAdmin || form.principalId === user?.principalId)}
              placeholder="2026_XXXX"
              data-testid="field-id"
            />
          </Field>
          <Field label="Client">
            <input value={form.client||""} onChange={(e)=>setField("client", e.target.value)} className={inp} data-testid="field-client" />
          </Field>
        </Row>
        <Row>
          <Field label="Start date"><input type="date" value={form.startDate||""} onChange={(e)=>setField("startDate", e.target.value)} className={inp} data-testid="field-start" /></Field>
          <Field label="Expected end date"><input type="date" value={form.endDate||""} onChange={(e)=>setField("endDate", e.target.value)} className={inp} data-testid="field-end" /></Field>
          <Field label="Status" info="In Progress = actively being worked on. On Hold = paused. Potential = opportunity not yet won. Completed = finished — the project is listed separately in the portfolio but still counts in every total, chart and history.">
            <select value={form.status||"In Progress"} onChange={(e)=>setField("status", e.target.value)} className={inp} data-testid="field-status">
              {PROJECT_STATUSES.map((s)=> <option key={s}>{s}</option>)}
            </select>
          </Field>
        <Row>
          <Field label="Currency" info="Fees are always entered and stored in NZD — NZD is the source of truth. Pick a client-facing currency and the equivalent is shown in small text beneath NZD figures. Firm-wide reporting stays in NZD.">
            <select
              value={form.currency || "NZD"}
              onChange={(e) => setForm((f) => ({
                ...f,
                currency: e.target.value,
                ...(e.target.value === "NZD" ? { fxRate: null, fxRateSetAt: null, fxRateSetBy: null } : {}),
              }))}
              className={inp}
              data-testid="field-currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}{ECB_UNSUPPORTED.includes(c) ? " · rate by hand" : ""}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Rate · 1 NZD ="
            info="LOCKED when saved. Forecast months convert at the live ECB rate shown alongside; months already gone and every invoiced figure stay at this locked rate, so nothing already reported can move. NZD amounts are always the stored truth."
          >
            <input
              type="number" step="0.0001" min="0"
              value={form.fxRate ?? ""}
              disabled={(form.currency || "NZD") === "NZD"}
              onChange={(e) => setForm((f) => ({
                ...f,
                fxRate: e.target.value === "" ? null : Number(e.target.value),
                fxRateSetAt: new Date().toISOString(),
                fxRateSetBy: user?.email || "",
                fxRateSource: "hand-entered",
                fxRateHistory: [
                  ...(f.fxRateHistory || []),
                  ...(f.fxRate && Number(f.fxRate) !== Number(e.target.value)
                    ? [{ rate: Number(f.fxRate), currency: f.currency, replacedAt: new Date().toISOString(), replacedBy: user?.email || "" }]
                    : []),
                ],
              }))}
              placeholder={(form.currency || "NZD") === "NZD" ? "—" : `${form.currency} per 1 NZD`}
              className={`${inp} disabled:opacity-40`}
              data-testid="field-fxrate"
            />
          </Field>
          <Field
            label="Live rate · ECB"
            info="The European Central Bank publishes reference rates once per working day, so on a Monday in NZ the latest available day is Friday's — the 'as at' date is always shown. Clicking 'Use today's rate' only fills the field; it never restates a figure you have already saved."
          >
            <div className="px-3 py-2 space-y-1" data-testid="fxrate-locked">
              {(form.currency || "NZD") === "NZD" ? (
                <div className="font-mono-num text-xs text-[var(--soft-fg)]">NZD project — no conversion</div>
              ) : (
                <>
                  <div className="font-mono-num text-[11px] text-[var(--ink)]" data-testid="fx-as-at">
                    {fxLiveLabel(form, fx) || "Live rate loading…"}
                  </div>
                  <div className="font-mono-num text-[10px] text-[var(--soft-fg)]" data-testid="fxrate-locked-line">
                    {form.fxRate
                      ? `Locked ${form.fxRate}${form.fxRateSetAt ? ` · ${new Date(form.fxRateSetAt).toLocaleDateString("en-NZ")}` : ""}${form.fxRateSetBy ? ` · ${form.fxRateSetBy.split("@")[0]}` : ""}${form.fxRateSource ? ` · ${form.fxRateSource}` : ""}`
                      : "No rate locked yet"}
                  </div>
                  {liveFxRate > 0 && (
                    <button
                      type="button"
                      onClick={applyLiveFxRate}
                      disabled={Number(form.fxRate) === liveFxRate}
                      className="px-2 py-1 border border-[var(--rule-strong)] text-[10px] font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] disabled:opacity-40"
                      data-testid="use-live-rate"
                    >
                      Use today’s rate
                    </button>
                  )}
                </>
              )}
            </div>
          </Field>
        </Row>

        </Row>
        <Row>
          <Field label="Risk flag" info="Flag this project as needing principal attention. Surfaces on the admin Overview so issues don't get lost.">
            <select value={form.riskFlag||"on_track"} onChange={(e)=>setField("riskFlag", e.target.value)} className={inp} data-testid="field-risk">
              {RISK_FLAGS.map((r)=> <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </Field>
          {(isAdmin || user?.role === "associate") ? (
            <Field label="Linked principal" info={isAdmin ? "Admins can assign a project to any principal in the firm. Principals cannot re-assign — their projects are always filed under themselves." : "Associates must pick which principal's portfolio this project joins. Once created it can only be reassigned by an admin or the owning principal."}>
              <select
                value={form.principalId || ""}
                onChange={(e) => setField("principalId", e.target.value)}
                className={inp}
                data-testid="field-principal"
                disabled={user?.role === "associate" && !!editing}
              >
                {isAdmin && <option value="">— Auto-link to me on save —</option>}
                {user?.role === "associate" && <option value="">— pick a principal —</option>}
                {(rawData?.principals || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>
          ) : (
            <div />
          )}
          <div />
        </Row>
      </Section>

      {/* Scope grid — one row per design stage. Contract status & probability
          live per-scope; the project-level Contract chip is derived on save.
          Sub-consultants sit in their own section below (deducted). */}
      <Section banner="Scope & monthly fees" title="Forecast · by stage" subtitle="One row per stage. Contract status, probability, notes per scope. The total below is the GROSS fee to the client — sub-consultants are deducted in the next section.">
        <ScopeGrid
          months={m}
          scopes={form.scopes}
          stages={STAGES}
          contractValues={CONTRACT_VALUES}
          onChange={(scopes) => setForm((f) => ({ ...f, scopes }))}
          onAdd={addScope}
          onAddSub={addSubRow}
          onRemove={removeScope}
          onAddSubset={addSubset}
          onMove={(from, to) => setForm((f) => ({ ...f, scopes: reorderScopes(f.scopes, from, to) }))}
          isAdmin={isAdmin}
          canEditPast={canEditPast}
          hideSub
        />
      </Section>

      {/* Sub-consultants — own section, deducted from the gross total */}
      <Section banner="Sub-consultants" title="Sub-consultants · deducted from the total" subtitle="Pass-through costs paid to sub-consultants. Enter them as positive figures — they are subtracted from the forecast total to give the net fee the firm keeps. The client is still invoiced the gross amount.">
        <SubConsultantsGrid
          months={m}
          scopes={form.scopes}
          contractValues={CONTRACT_VALUES}
          onChange={(scopes) => setForm((f) => ({ ...f, scopes }))}
          onAdd={addSubRow}
          onRemove={removeScope}
          canEditPast={canEditPast}
        />
      </Section>

      {/* NET figure — sits immediately above resourcing */}
      <NetFeeStrip
        gross={netFigures.gross}
        subs={netFigures.subs}
        net={netFigures.net}
        project={form}
        scopes={form.scopes}
        months={m}
        fx={fx}
      />

      {/* Profitability calculator — measured against the NET fee, above
          resourcing so the team is planned against what the firm keeps. */}
      <div data-testid="section-profitability">
        <div className="flex items-center gap-3 pb-2 border-b border-[var(--ink)] mb-4">
          <span className="inline-block w-2 h-2 bg-[var(--ink)]" aria-hidden />
          <span className="font-display text-[10px] tracked uppercase text-[var(--ink)]">Profitability · against net fee</span>
        </div>
        <ProfitabilityBar
          scopes={form.scopes}
          allocations={mergedAllocations(form)}
          people={people}
          rates={rates}
          sticky={false}
        />
      </div>

      {/* Merged project team — one grid for named people AND TBD-tier
          requests. Legacy `tierResourcing` rows are migrated into TBD
          allocation rows on load. */}
      <Section
        banner="Project team / resourcing"
        title="One team · named people or TBD by tier"
        subtitle={isAdmin
          ? "Who's on this project, month by month. Your edits apply to the capacity plan immediately."
          : "Who you want on this project. Proposals go to management for approval before they count on the capacity plan. TBD-by-tier rows go through the same approval — management assigns the named person on approval."}
      >
        {(form.allocations || []).some((r) => r.status === "proposed") && (
          <div className="mb-3 border border-[#8a6d1a]/40 bg-[#FBF6EA] text-[#7C3A06] px-3 py-2 text-xs font-mono-num" data-testid="pending-allocations-banner">
            Resourcing awaiting management approval — proposed people aren't confirmed on the capacity plan yet.
          </div>
        )}
        <PersonnelAllocations
          months={m}
          people={people}
          leave={rawData?.leave || {}}
          rates={rates}
          rows={form.allocations || []}
          onChange={(rows) => setForm((f) => ({ ...f, allocations: rows }))}
          isAdmin={isAdmin}
          canEditPast={canEditPast}
          rawData={rawData}
        />
      </Section>

      {/* Invoiced — gross stages only; the client is billed the full amount
          including sub-consultant costs, so the deduction never appears here */}
      <Section banner="Invoiced" title="Invoiced · by stage" subtitle="What was invoiced, by stage per month — works from the GROSS forecast total (sub-consultant costs are billed on to the client, so they are not deducted here). Contract value is shared with the forecast grid, so month cells accept a ratio (5/10 · 0.5) or dollars. Notes here are invoicing-only. Click a month's status pill to confirm.">
        <InvoiceActuals
          months={m}
          scopes={form.scopes}
          project={form}
          invoicingConfirmed={form.invoicingConfirmed || {}}
          hideSub
          onScopeInvoiced={(idx, mo, v, ratioStr) => setForm((f) => ({
            ...f,
            scopes: f.scopes.map((s, i) => {
              if (i !== idx) return s;
              const invoicedRatios = { ...(s.invoicedRatios || {}) };
              if (ratioStr) invoicedRatios[mo] = ratioStr;
              else delete invoicedRatios[mo];
              return { ...s, invoiced: { ...(s.invoiced || {}), [mo]: v }, invoicedRatios };
            }),
          }))}
          onScopeChange={(idx, patch) => setForm((f) => ({
            ...f,
            scopes: f.scopes.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
          }))}
          onConfirmMonth={(mo, next) => setForm((f) => {
            const nowIso = new Date().toISOString();
            const conf = { ...(f.invoicingConfirmed || {}) };
            if (next) {
              conf[mo] = { confirmedAt: nowIso, confirmedBy: user?.email || "" };
            } else {
              delete conf[mo];
            }
            return { ...f, invoicingConfirmed: conf };
          })}
        />
      </Section>

      {/* Invoice estimator — only shown when the project isn't fully signed.
          Once contracts are in place the fee is locked, so the estimator
          would just be noise. */}
      <InvoiceEstimator
        contract={deriveContract(form.scopes)}
        peopleNeededPerMonth={derivedPpl}
        totalFee={totalFee}
        onUseFigure={(amount) => {
          // Append a new scope row spreading the suggested fee evenly across
          // the 12 forecast months. Keeps existing scopes intact so the
          // principal can see exactly where the figure was added.
          const perMonth = Math.round((amount / m.length) * 100) / 100;
          const draft = {
            id: `s_${Math.random().toString(36).slice(2, 8)}`,
            stage: STAGES[0] || "Feasibility",
            contract: "Tentative",
            probability: 1.0,
            note: "Suggested invoice (driver-rate baseline)",
            revenue: Object.fromEntries(m.map((mo) => [mo, perMonth])),
          };
          setForm((f) => ({ ...f, scopes: [...f.scopes, draft] }));
          toast.success("Added a new scope row with the driver-rate baseline split across the year");
        }}
      />

      {/* Editors — admin + owning principal only. Personnel added here can be
          granted an "associate" login (from the Users page) that lets them
          open and edit just this project. */}
      {editing && (isAdmin || form.principalId === user?.principalId) && (
        <Section title="Editors" subtitle="People from Personnel who can open and edit this project when they have an associate login. Admins and the owning principal can also always edit.">
          <EditorsPanel
            personnel={rawData?.personnel || []}
            value={form.editors || []}
            onChange={(v) => setField("editors", v)}
          />
        </Section>
      )}

      {/* Notes */}
      <Section title="Notes / risks">
        <textarea value={form.note||""} onChange={(e)=>setField("note", e.target.value)} className="w-full p-3 border border-[var(--rule)] font-mono-num text-xs min-h-[120px] outline-none focus:border-[var(--ink)] bg-[var(--paper-soft)]" data-testid="field-note" />
      </Section>
    </div>
  );
}

const inp = "w-full px-3 py-2 border border-[var(--rule)] font-mono-num text-sm outline-none focus:border-[var(--ink)] bg-white";

// Driver-rate-based invoice estimator. Hidden once the project is fully
// signed ("Yes"). Calculates a suggested minimum invoice from the
// people-needed schedule and shows a soft warning if the typed fee is
// below the baseline.
function InvoiceEstimator({ contract, peopleNeededPerMonth, totalFee, onUseFigure }) {
  if (contract === "Yes") return null;
  const totalPeopleMonths = Object.values(peopleNeededPerMonth || {})
    .reduce((s, n) => s + (n || 0), 0);
  const suggested = totalPeopleMonths * DRIVER_RATE_PER_PERSON_MONTH;
  if (suggested <= 0) {
    return (
      <div className="border-l-4 border-[#B45309] bg-[#FBF3F2]/30 p-5" data-testid="invoice-estimator-empty">
        <div className="eyebrow text-[#B45309]">Suggested invoice · driver-rate estimate</div>
        <div className="text-xs text-[var(--soft-fg)] mt-2">
          Enter how many people the project needs each month above to see a suggested invoice
          based on the firm’s driver rate. The estimator stays hidden once contracts are signed.
        </div>
      </div>
    );
  }
  const below = totalFee > 0 && totalFee < suggested;
  return (
    <div className="border-l-4 border-[#B45309] bg-[#FBF3F2]/30 p-5" data-testid="invoice-estimator">
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          <div className="eyebrow text-[#B45309]">Suggested invoice · driver-rate estimate</div>
          <div className="font-mono-num text-3xl mt-1" data-testid="invoice-estimator-amount">
            {fmtMoney(suggested)}
          </div>
          <div className="text-xs text-[var(--soft-fg)] mt-2 max-w-xl">
            Based on <strong>{totalPeopleMonths.toFixed(1)}</strong> people-months × ${DRIVER_RATE_PER_PERSON_MONTH.toLocaleString()}/month driver rate
            (includes labour, overheads, and target margin). For reference only — does not auto-fill the fee.
          </div>
          {below && (
            <div className="mt-3 text-xs text-[#6B2F2A] inline-flex items-center gap-1.5" data-testid="invoice-below-warning">
              <AlertTriangle size={12} />
              Current total fee of <strong>{fmtMoney(totalFee)}</strong> is below the driver-rate estimate — project may not hit target margin.
            </div>
          )}
        </div>
        <button
          onClick={() => onUseFigure(suggested)}
          className="shrink-0 px-3 py-1.5 border border-[#B45309] text-[#B45309] text-xs font-display tracked-tight uppercase hover:bg-[#B45309] hover:text-white"
          data-testid="invoice-use-figure"
          title="Append a new scope row with this figure split evenly across the 12 months"
        >
          Use this figure
        </button>
      </div>
    </div>
  );
}

function Section({ title, subtitle, right, banner, children }) {
  return (
    <section className="space-y-3" data-testid={banner ? `section-${banner.toLowerCase().replace(/\s+/g, "-")}` : undefined}>
      {banner && (
        <div className="flex items-center gap-3 pb-2 border-b border-[var(--ink)]">
          <span className="inline-block w-2 h-2 bg-[var(--ink)]" aria-hidden />
          <span className="font-display text-[10px] tracked uppercase text-[var(--ink)]">{banner}</span>
        </div>
      )}
      <div className="flex justify-between items-end">
        <div>
          <h3 className="h-section">{title}</h3>
          {subtitle && <div className="t-caption normal-case tracking-[0.06em] mt-1 max-w-3xl leading-relaxed">{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}
function Row({ children }) { return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>; }
function Field({ label, children, className="", info }) {
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow inline-flex items-center">
        {label}
        {info && <InfoTip side="bottom" label={label}>{info}</InfoTip>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

// Editors panel — visible to admin + owning principal only. Adds/removes
// personnel IDs from project.editors. Server enforces that associates whose
// linked personnelId is in this list may open and edit the project.
function EditorsPanel({ personnel, value, onChange }) {
  const [pick, setPick] = useState("");
  const list = value || [];
  const available = (personnel || []).filter((p) => !list.includes(p.id));
  const nameOf = (id) => (personnel || []).find((p) => p.id === id)?.fullName || id;
  return (
    <div className="space-y-2" data-testid="editors-panel">
      <div className="flex flex-wrap gap-1.5">
        {list.length === 0 && (
          <span className="text-xs text-[var(--soft-fg)]">No editors yet — associates with the matching personnel record won't see this project.</span>
        )}
        {list.map((pid) => (
          <span key={pid} className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--rule-strong)] text-[11px] font-mono-num bg-[var(--paper-soft)]" data-testid={`editor-chip-${pid}`}>
            {nameOf(pid)}
            <button
              type="button"
              onClick={() => onChange(list.filter((x) => x !== pid))}
              className="text-[var(--soft-fg)] hover:text-[#6B2F2A]"
              aria-label={`Remove ${nameOf(pid)}`}
              data-testid={`editor-remove-${pid}`}
            >×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 items-center pt-1">
        <select
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          className="border border-[var(--rule)] p-2 text-xs font-mono-num flex-1"
          data-testid="editors-picker"
        >
          <option value="">— pick personnel to add —</option>
          {available.map((p) => <option key={p.id} value={p.id}>{p.fullName} · {p.tier}</option>)}
        </select>
        <button
          type="button"
          disabled={!pick}
          onClick={() => { onChange([...list, pick]); setPick(""); }}
          className="px-3 py-1.5 border border-[var(--rule-strong)] text-[11px] font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] disabled:opacity-40"
          data-testid="editors-add"
        >Add editor</button>
      </div>
    </div>
  );
}
